import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { X, Gift, Clock, Sparkles, CheckCircle, AlertCircle } from 'lucide-react';
import { API } from '@/App';

const FIRST_VISIT_KEY = 'first_visit_timestamp';
const PROMO_DISMISSED_KEY = 'promo_dismissed';
const PROMO_CODE = 'WELCOME25';

const FirstTimePromo = ({ onClose, userEmail }) => {
  const [showPromo, setShowPromo] = useState(false);
  const [copied, setCopied] = useState(false);
  const [validationStatus, setValidationStatus] = useState(null); // null, 'valid', 'used', 'invalid'
  const [discountValue, setDiscountValue] = useState(25);

  useEffect(() => {
    // Check if this is a first-time visitor and promo hasn't been dismissed
    const firstVisit = localStorage.getItem(FIRST_VISIT_KEY);
    const promoDismissed = localStorage.getItem(PROMO_DISMISSED_KEY);
    
    if (!firstVisit) {
      // First time visitor - set the timestamp
      localStorage.setItem(FIRST_VISIT_KEY, new Date().toISOString());
      // Show promo with a small delay
      const timer = setTimeout(() => {
        setShowPromo(true);
        validateCode();
      }, 2000);
      return () => clearTimeout(timer);
    } else if (!promoDismissed) {
      // Returning visitor who hasn't dismissed the promo yet
      // Check if within 24 hours of first visit
      const firstVisitDate = new Date(firstVisit);
      const now = new Date();
      const hoursSinceFirstVisit = (now - firstVisitDate) / (1000 * 60 * 60);
      
      if (hoursSinceFirstVisit < 24) {
        const timer = setTimeout(() => {
          setShowPromo(true);
          validateCode();
        }, 2000);
        return () => clearTimeout(timer);
      }
    }
  }, []);

  const validateCode = async () => {
    try {
      // Quick check if code is active
      const response = await fetch(`${API}/api/promo/check/${PROMO_CODE}`);
      const data = await response.json();
      
      if (data.exists && data.active) {
        setDiscountValue(data.discount_value || 25);
        setValidationStatus('valid');
      } else {
        setValidationStatus('invalid');
      }
    } catch (error) {
      console.error('Failed to validate promo code:', error);
      setValidationStatus('valid'); // Default to showing the promo
    }
  };

  const handleDismiss = () => {
    localStorage.setItem(PROMO_DISMISSED_KEY, 'true');
    setShowPromo(false);
    if (onClose) onClose();
  };

  const handleCopyCode = () => {
    navigator.clipboard.writeText(PROMO_CODE);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!showPromo || validationStatus === 'invalid') return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      data-testid="first-time-promo"
    >
      <div className="relative w-full max-w-md bg-white dark:bg-slate-900 rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-300">
        {/* Header Gradient */}
        <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-br from-[#0047AB] via-[#0066CC] to-[#0047AB]" />
        
        {/* Close Button */}
        <button
          onClick={handleDismiss}
          className="absolute top-3 right-3 z-10 p-1.5 rounded-full bg-white/20 hover:bg-white/30 text-white transition-colors"
          data-testid="promo-close-btn"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Content */}
        <div className="relative pt-8 pb-6 px-6">
          {/* Icon */}
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 rounded-full bg-white shadow-lg flex items-center justify-center">
              <Gift className="w-8 h-8 text-[#0047AB]" />
            </div>
          </div>

          {/* Welcome Text */}
          <div className="text-center mt-8">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-100 text-amber-700 text-xs font-medium mb-3">
              <Sparkles className="w-3 h-3" />
              First-Time Visitor Exclusive
            </div>
            
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
              Welcome to Exam Trainer!
            </h2>
            
            <p className="text-slate-600 dark:text-slate-400 mb-6">
              Start your certification journey with a special discount
            </p>

            {/* Discount Box */}
            <div className="bg-gradient-to-r from-[#0047AB]/10 to-green-500/10 rounded-xl p-4 mb-4">
              <div className="text-4xl font-bold text-[#0047AB] mb-1">
                {discountValue}% OFF
              </div>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                on your first Pro subscription
              </p>
            </div>

            {/* Promo Code */}
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className="flex items-center gap-2 px-4 py-2 bg-slate-100 dark:bg-slate-800 rounded-lg border-2 border-dashed border-slate-300 dark:border-slate-600">
                <span className="font-mono font-bold text-lg tracking-wider">{PROMO_CODE}</span>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={handleCopyCode}
                className="shrink-0"
              >
                {copied ? 'Copied!' : 'Copy'}
              </Button>
            </div>

            {/* Validation Notice */}
            <div className="flex items-center justify-center gap-2 text-sm text-green-600 mb-2">
              <CheckCircle className="w-4 h-4" />
              <span>Code validated - one-time use per account</span>
            </div>

            {/* Timer Notice */}
            <div className="flex items-center justify-center gap-2 text-sm text-slate-500 mb-6">
              <Clock className="w-4 h-4" />
              <span>Valid for first 24 hours only</span>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col gap-2">
              <Button
                onClick={handleDismiss}
                className="w-full bg-[#0047AB] hover:bg-[#003380] text-white"
              >
                Continue to Dashboard
              </Button>
              <button
                onClick={handleDismiss}
                className="text-sm text-slate-500 hover:text-slate-700"
              >
                Maybe later
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FirstTimePromo;
